<template>
  <div class="space-y-4">
    <UiInputSelectVee
      name="qaMode"
      :label="$t('W: QA-godkendelse kræves')"
      :options="modeOptions"
      :modelValue="qaMode"
      @update:model-value="onModeChange" />

    <UiComboboxMultiVee
      v-if="qaMode === 'answers'"
      name="qaAnswerIDs"
      :label="$t('W: Svar der kræver QA')"
      :placeholder="$t('W: Vælg svar')"
      :options="answerOptions"
      :rules="['required']"
      isRequired
      :modelValue="qaAnswerIDs"
      @update:model-value="$emit('update:qaAnswerIDs', $event)" />
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { mapState } from 'pinia'
import type { IAnalysisTestAnswer } from '@team-milva/lab-models/resources'
import { getTextObjValue } from '@team-milva/lab-models/textObj'
import { useAppStore } from '@/stores/appStore'

type TQaMode = '' | 'always' | 'answers'

/**
 * The QA setting of a single-select test (MIL-551) — the counterpart of the yes/no test's "QA-godkendelse
 * kræves" dropdown, shared by the edit pane's Details tab, the focus view and the create-test wizard.
 * Aldrig / Altid / Ved bestemte svar; the last shows the answers that make a result need QA.
 *
 * The picker offers the active answers plus the ones already selected, so an answer deactivated after
 * it was picked can still be taken out, but once taken out it cannot be chosen again.
 */
export default {
  name: 'AnalysisTestQaSetting',

  emits: ['update:qaMode', 'update:qaAnswerIDs'],

  props: {
    answers: {
      type: Array as PropType<IAnalysisTestAnswer[]>,
      required: true,
    },
    qaMode: {
      type: String as PropType<TQaMode>,
      required: true,
    },
    qaAnswerIDs: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale']),

    modeOptions(): { value: TQaMode; text: string }[] {
      return [
        { value: '', text: this.$t('W: Aldrig') },
        { value: 'always', text: this.$t('W: Altid') },
        { value: 'answers', text: this.$t('W: Ved bestemte svar') },
      ]
    },

    answerOptions(): { value: string; text: string; subtitle?: string }[] {
      return this.answers
        .filter((x) => x.activeState === 'active' || this.qaAnswerIDs.includes(x.id))
        .map((answer) => ({
          value: answer.id,
          text: getTextObjValue(answer.text, this.currentLocale) || this.$t('W: (uden tekst)'),
          ...(answer.activeState === 'inactive' ? { subtitle: this.$t('W: Inaktiv') } : {}),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
  },

  watch: {
    // A newly added answer can be removed again before the test is saved. Its ID has to leave the
    // selection with it: left behind, it keeps `required` satisfied while the save strips it and
    // sends an empty list under `qaTrigger: 'answers'`. A deactivated answer stays in `answers`, so
    // its selection is kept — only an answer that is gone is pruned.
    answers: {
      deep: true,
      handler(): void {
        const known = new Set(this.answers.map((x) => x.id))
        const kept = this.qaAnswerIDs.filter((id) => known.has(id))
        if (kept.length !== this.qaAnswerIDs.length) {
          this.$emit('update:qaAnswerIDs', kept)
        }
      },
    },
  },

  methods: {
    onModeChange(mode: string): void {
      this.$emit('update:qaMode', mode as TQaMode)
    },
  },
}
</script>
